import { useDynamicCallback } from '@src/hooks/useDynamicCallback';
import type { CustomCellEditorProps } from 'ag-grid-react';
import { useGridCellEditor } from 'ag-grid-react';
import type { BigSource } from 'big.js';
import { useRef } from 'react';
import type { FormControlSizes } from '../Form/types';
import { AmountInput, type AmountInputProps } from '../Forms/AmountInput';

export interface AgGridAmountInputProps extends Pick<AmountInputProps, 'style'> {
  currencyField?: string;
  size?: FormControlSizes;
}

/**
 * AG Grid can give this editor either the raw primitive cell value or an amount-style
 * object from blotter columns such as size (`{ value, currency, increment }`).
 * `AmountInput` is controlled by a string, so nullish/empty values become `''` while
 * valid primitive or wrapped values are preserved as their input text.
 */
function resolveAmountEditorValue(value: unknown): string {
  if (value == null) {
    return '';
  }
  if (typeof value === 'object' && 'value' in value) {
    const inner = (value as { value: BigSource }).value;
    if (inner == null || inner === '') {
      return '';
    }
    return typeof inner === 'string' ? inner : inner.toString();
  }
  return typeof value === 'string' ? value : String(value);
}

export const AgGridAmountInput = function AgGridAmountInput({
  size,
  node,
  colDef,
  parseValue,
  currencyField = 'Currency',
  data,
  value,
  onValueChange,
  stopEditing,
  ...props
}: CustomCellEditorProps & AgGridAmountInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { currenciesBySymbol } = props.context.current;

  const handleChange = useDynamicCallback((value: string | undefined) => {
    onValueChange(value ? parseValue(value) : undefined);

    // Weird cursor behavior if user types the negative '-' sign
    // Similar to packages/kyoko/src/components/Form/NumberInput/useNumberInput.tsx
    // So we purposely move the cursor to the end
    if (value === '-') {
      setTimeout(() => inputRef.current?.setSelectionRange(100, 100), 0);
    }
  });

  useGridCellEditor({
    afterGuiAttached() {
      inputRef.current?.focus();
    },
    focusIn() {
      inputRef.current?.focus();
    },
  } as Parameters<typeof useGridCellEditor>[0]);

  const asset = currenciesBySymbol?.get(data?.[currencyField]);
  const resolvedValue = resolveAmountEditorValue(value);

  return (
    <AmountInput
      ref={inputRef}
      onChange={handleChange}
      onBlur={() => stopEditing()}
      value={resolvedValue}
      currency={data?.[currencyField]}
      asset={asset}
      style={{
        width: '100%',
        height: `${node.rowHeight}px`,
        background: props.context.current.theme?.backgroundBody,
        ...props.style,
      }}
    />
  );
};
